/**
 * mockSalesUniBoxApi — adapts n8nServiceMock to the UniBoxApi contract
 * (getFeed / getThread / getSuggestions) that the UniBox engine consumes.
 *
 * This is the seam where schema-accurate mock data plugs into the Sales
 * Intelligence tab. Each method calls the corresponding mock N8N action and
 * maps the response into the UniBox render shape via leadToUniBoxMapper.
 */

import type {
  UniBoxApi,
  UniBoxFeed,
  UniBoxEntity,
  UniBoxGroup,
  UniBoxThread,
} from '../../components/UniBox/types';
import { n8nServiceMock } from './mockN8nService';
import {
  mapLeadCardToUniBoxEntity,
  mapCampaignToUniBoxGroup,
} from './leadToUniBoxMapper';
import { SUGGESTIONS, OUTREACH_BODIES } from './dictionaries';

const emptyFeed = (): UniBoxFeed => ({ campaigns: [], leads: [] });

/**
 * Build the UniBoxApi for the Sales Intelligence tab. `userEmail` identifies
 * the owner of the generated dataset (mirrors the real client_email param).
 */
export const createMockSalesApi = (userEmail: string): UniBoxApi => ({
  /**
   * Load the whole Sales board: every campaign (as groups) + every lead across
   * all campaigns (as entities). UniBox splits these by shape via isGroup /
   * isEntity (UniBox.tsx:131).
   */
  getFeed: async (): Promise<UniBoxFeed> => {
    const metricsResponse = await n8nServiceMock.getCampaignMetrics({ client_email: userEmail });
    if (!metricsResponse.success || !metricsResponse.data) return emptyFeed();

    const groups: UniBoxGroup[] = [];
    const entities: UniBoxEntity[] = [];

    for (const campaign of metricsResponse.data.campaigns) {
      const leadsResponse = await n8nServiceMock.getLeadsCards({
        campaign_id: campaign.campaign_id,
        client_email: userEmail,
      });
      const leads = leadsResponse.data?.leads || [];
      groups.push(mapCampaignToUniBoxGroup(campaign, leads));
      leads.forEach((card) => entities.push(mapLeadCardToUniBoxEntity(card)));
    }

    return { campaigns: groups, leads: entities } as unknown as UniBoxFeed;
  },

  /**
   * Build a small conversation thread for a lead + channel. Outreaching leads
   * get their sent sequence steps as outbound messages; engaged leads get a
   * seeded inbound reply too.
   */
  getThread: async (lvl1_id: number, channel: string): Promise<UniBoxThread> => {
    // Resolve the entity behind the thread so the messages match its sequence.
    const feed = await createMockSalesApi(userEmail).getFeed();
    const entity = (feed.leads as unknown as UniBoxEntity[]).find((e) => e.lvl1_id === lvl1_id);
    const steps = entity?.sequenceSteps?.filter((s) => s.channel === channel) || [];

    const messages: UniBoxThread['messages'] = steps
      .filter((s) => s.sentAt)
      .map((s, i) => ({
        id: i + 1,
        direction: 'outbound',
        state: 'sent',
        sent_by: 'ai',
        sequence_step: s.step,
        body: s.body,
        sent_at: s.sentAt || undefined,
      }));

    // Engaged leads have a recent inbound reply on their primary channel.
    if (entity && entity.status === 'engaged' && entity.channels[0] === channel) {
      messages.push({
        id: messages.length + 1,
        direction: 'inbound',
        state: 'received',
        body: 'Interesting — can you send a one-pager?',
        received_at: entity.last_activity_at,
      });
    }

    return { lvl1_id, channel, control_state: entity?.control_state, messages };
  },

  /** Two seeded AI suggestions per lead+channel. */
  getSuggestions: async (_lvl1_id: number, _channel: string): Promise<string[]> => [
    SUGGESTIONS[0],
    OUTREACH_BODIES[1].replace(/\{\{firstName\}\}/g, 'there'),
  ],
});
